// Quote Builder — SJB board colour / hinge / drawer controls.
//
// Two mounts, one contract (see sjb-catalogue.ts for the reasoning):
//
//   mountSjbDefaults    — the QUOTE-level strip. Sets the values new lines
//                         inherit. Never rewrites existing lines.
//   mountSjbLineEditor  — the PER-LINE override. Re-prices exactly one line.
//
// Mirrors the mount/paint/wire idiom of session-defaults.ts so the two strips
// read the same, but persists to the quote's `defaults` JSONB rather than
// localStorage — the colour drives line pricing, so it is business data.

import { escapeHtml } from "./formatters";
import type { QuoteStore } from "./state";
import type { QuoteItem } from "./types";
import {
  getSjbDefaults,
  setSjbDefaults,
  loadSjbColours,
  edgingKeyForLabel,
  SJB_EDGING_MATCHING,
  repriceSjbItem,
  SJB_SUPPLIER,
  type SjbColour,
} from "./sjb-catalogue";
import { mountColourCombobox, type ColourComboHandle } from "./sjb-colour-combobox";

export interface SjbStripHandle { unmount(): void; }

const HINGE_OPTS: Array<[string, string]> = [["standard", "Standard"], ["onyx", "Onyx"]];
const DRAWER_OPTS: Array<[string, string]> = [["antaro", "Antaro (30kg)"], ["legrabox", "Legrabox (40kg)"]];

function selectOptions(opts: Array<[string, string]>, selected: string): string {
  return opts.map(([v, l]) =>
    `<option value="${v}"${v === selected ? " selected" : ""}>${escapeHtml(l)}</option>`).join("");
}

// ── Quote-level defaults strip ─────────────────────────────────────────────

export function mountSjbDefaults(host: HTMLElement, store: QuoteStore): SjbStripHandle {
  let mounted = true;
  let colours: SjbColour[] = [];
  let combo: ColourComboHandle | null = null;
  let edgingCombo: ColourComboHandle | null = null;

  function paint(): void {
    if (!mounted) return;
    const d = getSjbDefaults(store);
    if (!colours.length) {
      host.innerHTML =
        `<div class="qb-session" data-role="sjb-defaults">
           <label class="qb-session-lbl">Board colour (quote default)</label>
           <div class="qb-session-loading">Loading SJB colours…</div>
         </div>`;
      return;
    }
    host.innerHTML = `
      <div class="qb-session" data-role="sjb-defaults" data-supplier="${escapeHtml(SJB_SUPPLIER)}">
        <div data-role="sjb-colour-slot"></div>
        <label class="qb-session-lbl">Hinge finish</label>
        <select class="qb-session-select" data-role="sjb-hinge">${selectOptions(HINGE_OPTS, d.hingeFinish)}</select>
        <label class="qb-session-lbl">Drawer system</label>
        <select class="qb-session-select" data-role="sjb-drawer">${selectOptions(DRAWER_OPTS, d.drawerSystem)}</select>
        <div data-role="sjb-edging-slot"></div>
        <p class="qb-session-note">Applies to cabinets added from now on. Lines already on this quote keep the colour they were added with — change those on the line itself.</p>
      </div>`;
    const slot = host.querySelector<HTMLElement>("[data-role='sjb-colour-slot']");
    if (slot) {
      combo = mountColourCombobox(slot, {
        colours, selectedKey: d.boardKey,
        label: "Board colour (quote default)",
        role: "sjb-colour",
        onPick: (c) => { setSjbDefaults(store, { boardKey: c.key, boardLabel: c.label }); paint(); },
      });
    }
    const eSlot = host.querySelector<HTMLElement>("[data-role='sjb-edging-slot']");
    if (eSlot) {
      if (edgingCombo) { edgingCombo.unmount(); edgingCombo = null; }
      edgingCombo = mountColourCombobox(eSlot, {
        colours,
        selectedKey: edgingKeyForLabel(d.edgingColour, colours),
        label: "Front edging colour (quote default)",
        role: "sjb-edging",
        // Same 614 decors, none of the pricing — see SJB_EDGING_MATCHING.
        flat: true,
        placeholder: SJB_EDGING_MATCHING,
        pinned: { key: SJB_EDGING_MATCHING, label: SJB_EDGING_MATCHING },
        onPick: (c) => {
          setSjbDefaults(store, {
            edgingColour: c.key === SJB_EDGING_MATCHING ? SJB_EDGING_MATCHING : c.label,
          });
          paint();
        },
      });
    }
    wire();
  }

  function wire(): void {
    host.querySelector<HTMLSelectElement>("[data-role='sjb-hinge']")
      ?.addEventListener("change", (e) => {
        setSjbDefaults(store, { hingeFinish: (e.target as HTMLSelectElement).value as "standard" | "onyx" });
      });
    host.querySelector<HTMLSelectElement>("[data-role='sjb-drawer']")
      ?.addEventListener("change", (e) => {
        setSjbDefaults(store, { drawerSystem: (e.target as HTMLSelectElement).value as "antaro" | "legrabox" });
      });

  }

  paint();
  void loadSjbColours().then((list) => {
    if (!mounted) return;
    colours = list;
    paint();
  });

  return {
    unmount(): void {
      mounted = false;
      if (edgingCombo) { edgingCombo.unmount(); edgingCombo = null; }
      if (combo) { combo.unmount(); combo = null; }
      host.innerHTML = "";
    },
  };
}

// ── Per-line override ──────────────────────────────────────────────────────

/**
 * Mount the colour / hinge / drawer controls for ONE line.
 *
 * Re-prices that line and writes it back by id. Reads no other line and
 * writes no quote default, so editing a line cannot drift the rest of the
 * quote — and the quote default cannot drift this line.
 */
export function mountSjbLineEditor(
  host: HTMLElement,
  store: QuoteStore,
  itemId: string,
): SjbStripHandle {
  let mounted = true;
  let colours: SjbColour[] = [];
  let combo: ColourComboHandle | null = null;
  let busy = false;

  const findItem = (): QuoteItem | null => {
    for (const z of store.get().zones) {
      const hit = z.items.find((i) => i.id === itemId);
      if (hit) return hit;
    }
    return null;
  };

  function paint(): void {
    if (!mounted) return;
    if (combo) { combo.unmount(); combo = null; }
    const item = findItem();
    if (!item) { host.innerHTML = ""; return; }
    const hardware = (item.sjbHardware ?? []).map((h) =>
      `<li>${escapeHtml(h.desc)} <span class="qb-line-code">${escapeHtml(h.code)}</span> ` +
      `× ${h.qty} @ £${h.unitPrice.toFixed(2)} = £${h.price.toFixed(2)}</li>`).join("");

    host.innerHTML = `
      <div class="qb-line-sjb" data-role="sjb-line" data-item="${escapeHtml(itemId)}">
        <div class="qb-line-breakdown">
          <div class="qb-line-row">
            <span>Carcass ${escapeHtml(item.code)}</span>
            <span>£${(item.listPrice || 0).toFixed(2)}
              ${item.sjbDiscountLabel ? `<em class="qb-line-badge">${escapeHtml(item.sjbDiscountLabel)}</em>` : ""}
              → £${((item.tradePrice || 0) - (item.sjbHardwareTotal || 0)).toFixed(2)}</span>
          </div>
          <div class="qb-line-row">
            <span>Hardware (at full list — no board discount)</span>
            <span>£${(item.sjbHardwareTotal || 0).toFixed(2)}</span>
          </div>
          ${hardware ? `<ul class="qb-line-hardware">${hardware}</ul>` : ""}
          <div class="qb-line-row qb-line-total">
            <span>Trade</span><span>£${(item.tradePrice || 0).toFixed(2)}</span>
          </div>
        </div>
        <div data-role="line-colour-slot"></div>
        <label class="qb-session-lbl">Hinge finish</label>
        <select class="qb-session-select" data-role="line-hinge"${busy ? " disabled" : ""}>
          ${selectOptions(HINGE_OPTS, item.sjbHingeFinish ?? "standard")}
        </select>
        <label class="qb-session-lbl">Drawer system</label>
        <select class="qb-session-select" data-role="line-drawer"${busy ? " disabled" : ""}>
          ${selectOptions(DRAWER_OPTS, item.sjbDrawerSystem ?? "antaro")}
        </select>
      </div>`;
    const slot = host.querySelector<HTMLElement>("[data-role='line-colour-slot']");
    if (slot && !busy) {
      combo = mountColourCombobox(slot, {
        colours,
        selectedKey: item.sjbBoardKey ?? "",
        label: "Board colour (this line)",
        role: `line-colour-${itemId}`,
        onPick: (c) => void apply({ boardKey: c.key, boardLabel: c.label }),
      });
    }
    wire();
  }

  async function apply(patch: Parameters<typeof repriceSjbItem>[1]): Promise<void> {
    const item = findItem();
    if (!item || busy) return;
    busy = true;
    paint();
    try {
      const next = await repriceSjbItem(item, patch);
      store.set((draft) => {
        for (const z of draft.zones) {
          const idx = z.items.findIndex((i) => i.id === itemId);
          if (idx >= 0) { z.items[idx] = next; return; }
        }
      });
    } finally {
      busy = false;
      paint();
    }
  }

  function wire(): void {
    host.querySelector<HTMLSelectElement>("[data-role='line-hinge']")
      ?.addEventListener("change", (e) => {
        void apply({ hingeFinish: (e.target as HTMLSelectElement).value as "standard" | "onyx" });
      });
    host.querySelector<HTMLSelectElement>("[data-role='line-drawer']")
      ?.addEventListener("change", (e) => {
        void apply({ drawerSystem: (e.target as HTMLSelectElement).value as "antaro" | "legrabox" });
      });
  }

  paint();
  void loadSjbColours().then((list) => {
    if (!mounted) return;
    colours = list;
    paint();
  });

  return {
    unmount(): void {
      mounted = false;
      if (combo) { combo.unmount(); combo = null; }
      host.innerHTML = "";
    },
  };
}
